import { useEffect, useState } from 'react'
import clsx from 'clsx'
import { TrashIcon } from '../../../../ui/icons/trash'
import { ButtonComponent } from '../../../../ui/molecules/button/newButton'
import { SelectCustom } from '../../../../ui/molecules/select'
import { OptionType } from '../../../../utils'
import { useAppDispatch, useAppSelector } from '../../../../lib/redux/hooks'
import { GetAllEntryProtocolAttachments, resetGetAllEntryProtocolAttachmentsStatus } from '../../../protocol/entry/slice'
import { addUpdateRIDDefaultAttachmentId, addUpdateRIDDefaultDocumentNote, setUpdateRIDAttachmentIdByIndex, setUpdateRIDAttachmentIds, setUpdateRIDDocumentNoteByIndex, setUpdateRIDDocumentNotes } from '../slice'
import { CompilationStateDTO } from '../dataService/dto'
import TextArea from '../../../../ui/molecules/textArea'

export function RightAddRID() {
    const dispatch = useAppDispatch()
    const ridState = useAppSelector(state => state.rid)
    const entryProtocolState = useAppSelector(state => state.entryProtocol)
    const [attachments, setAttachments] = useState<OptionType[]>([])

    const NoAvailableAttachments = () => {
        return attachments.filter(att => ridState.ridUpdateRequest.attachmentIds.includes(att.value))
    }

    const onCompilationState = (state: CompilationStateDTO | undefined) => {
        return state === CompilationStateDTO.COMPLETATA ? 'COMPLETATA' : 'IN CORSO'
    }

    useEffect(() => {
        dispatch(GetAllEntryProtocolAttachments({ page: 0, itemsPerPage: 0 }))
    }, [])

    useEffect(() => {
        if (entryProtocolState.getAllEntryProtocolAttachmentsStatus === 'successfully' &&
            entryProtocolState.getAllEntryProtocolAttachmentsResponse !== undefined
        ) {
            dispatch(resetGetAllEntryProtocolAttachmentsStatus())
            const data = entryProtocolState.getAllEntryProtocolAttachmentsResponse?.data.map(att => {
                return {
                    value: att.id,
                    label: `${att.description} [${att.coding}]`
                } as OptionType
            })
            setAttachments(data)
        }
    }, [entryProtocolState.getAllEntryProtocolAttachmentsStatus])

    return (
        <div className='flex flex-col gap-5 w-full'>
            <div className='flex flex-col gap-3'>
                <span className='text-sm font-semibold uppercase tracking-widest text-brandPrimary-600'>R.I.D. / Stato richiesta</span>

                <div className='rounded-xl border border-neutral-200 bg-white p-4'>
                    <div className='grid grid-cols-2 gap-3'>
                        <div className='rounded-lg border border-neutral-200 bg-neutral-50 p-3'>
                            <div className='text-xs font-semibold uppercase tracking-wide text-neutral-500'>Compilazione</div>
                            <div className={clsx('mt-1 text-sm font-semibold', ridState.getRidByIdResponse?.compilationState !== 'IN_CORSO' ? 'text-[#26AB45]' : 'text-[#FF941A]')}>
                                {onCompilationState(ridState.getRidByIdResponse?.compilationState as CompilationStateDTO)}
                            </div>
                        </div>

                        <div className='rounded-lg border border-neutral-200 bg-neutral-50 p-3'>
                            <div className='text-xs font-semibold uppercase tracking-wide text-neutral-500'>Ricevuta</div>
                            <div className={clsx('mt-1 text-sm font-semibold', ridState.getRidByIdResponse?.requestState === 'APERTA' ? 'text-[#FF941A]' : 'text-[#26AB45]')}>
                                {ridState.getRidByIdResponse?.requestState}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className='border-t border-neutral-200' />

            <div className='flex flex-col gap-3'>
                <span className='text-sm font-semibold uppercase tracking-widest text-brandPrimary-600'>R.I.D. / Documenti richiesti</span>

                {
                    ridState.ridUpdateRequest.attachmentIds &&
                    ridState.ridUpdateRequest.attachmentIds.map((att, index) => (
                        <div key={`att-${index}`} className='rounded-xl border border-neutral-200 bg-white p-3'>
                            <div className='flex gap-2 items-start'>
                                <div className='flex-1'>
                                    <SelectCustom
                                        disabled={ridState.getRidByIdResponse?.compilationState === CompilationStateDTO.COMPLETATA}
                                        value={att}
                                        options={attachments}
                                        disabledOptions={NoAvailableAttachments()}
                                        placeholder='Seleziona documento...'
                                        onChange={(value) => dispatch(setUpdateRIDAttachmentIdByIndex({ data: value, index: index }))}
                                    />
                                </div>
                                <button
                                    className='p-2 rounded-full border border-red-200 bg-red-50 hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                                    disabled={ridState.getRidByIdResponse?.compilationState === CompilationStateDTO.COMPLETATA}
                                    onClick={() => {
                                        if (ridState.getRidByIdResponse?.compilationState !== CompilationStateDTO.COMPLETATA)
                                            dispatch(setUpdateRIDAttachmentIds(ridState.ridUpdateRequest.attachmentIds.filter(attId => attId !== att)))
                                    }}
                                >
                                    <TrashIcon color='red' size={18} />
                                </button>
                            </div>
                        </div>
                    ))
                }
                {
                    ridState.ridUpdateRequest.attachmentIds.length === 0 && (
                        <div className='text-center text-sm text-neutral-500 py-4 rounded-xl border border-dashed border-neutral-300 bg-white'>
                            Nessun documento richiesto
                        </div>
                    )
                }
                <div className='flex justify-end'>
                    <ButtonComponent
                        disabled={ridState.getRidByIdResponse?.compilationState === CompilationStateDTO.COMPLETATA}
                        iconPosition='off'
                        variant='outline'
                        color='blue'
                        label='Aggiungi documento'
                        size='sm'
                        onClick={() => dispatch(addUpdateRIDDefaultAttachmentId())}
                    />
                </div>
            </div>
            <div className='border-t border-neutral-200' />
            <div className='flex flex-col gap-3'>
                <span className='text-sm font-semibold uppercase tracking-widest text-brandPrimary-600'>R.I.D. / Documenti liberi</span>

                {
                    ridState.ridUpdateRequest.documentNotes &&
                    ridState.ridUpdateRequest.documentNotes.map((note, index) => (
                        <div key={`note-${index}`} className='rounded-xl border border-neutral-200 bg-white p-3'>
                            <div className='flex gap-2 items-start'>
                                <div className='flex-1'>
                                    <TextArea
                                        value={ridState.ridUpdateRequest.documentNotes[index]}
                                        disabled={ridState.getRidByIdResponse?.compilationState === CompilationStateDTO.COMPLETATA}
                                        onChangeText={(text) => dispatch(setUpdateRIDDocumentNoteByIndex({ data: text, index: index }))}
                                    />
                                </div>
                                <button
                                    className='p-2 rounded-full border border-red-200 bg-red-50 hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                                    disabled={ridState.getRidByIdResponse?.compilationState === CompilationStateDTO.COMPLETATA}
                                    onClick={() => {
                                        if (ridState.getRidByIdResponse?.compilationState !== CompilationStateDTO.COMPLETATA)
                                            dispatch(setUpdateRIDDocumentNotes(ridState.ridUpdateRequest.documentNotes.filter(doc => doc !== note)))
                                    }}
                                >
                                    <TrashIcon color='red' size={18} />
                                </button>
                            </div>
                        </div>
                    ))
                }
                {
                    ridState.ridUpdateRequest.documentNotes.length === 0 && (
                        <div className='text-center text-sm text-neutral-500 py-4 rounded-xl border border-dashed border-neutral-300 bg-white'>
                            Nessun documento libero inserito
                        </div>
                    )
                }
                <div className='flex justify-end'>
                    <ButtonComponent
                        disabled={ridState.getRidByIdResponse?.compilationState === CompilationStateDTO.COMPLETATA}
                        iconPosition='off'
                        variant='outline'
                        color='blue'
                        label='Aggiungi documento libero'
                        size='sm'
                        onClick={() => dispatch(addUpdateRIDDefaultDocumentNote())}
                    />
                </div>
            </div>
        </div>
    )
}